import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-date-picker',
  templateUrl: './app-date-picker.component.html',
  styleUrls: ['./app-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDatePickerComponent),
      multi: true,
    },
  ],
})
export class AppDatePickerComponent implements ControlValueAccessor {
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  @ViewChild('dateInput') dateInputRef!: ElementRef<HTMLInputElement>;

  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() optionalText: string = '(optional)';
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  @Input() placeholder: string = 'Select date';
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() errorText: string | null = null;
  @Input() helperText: string = '';

  @Input() size: 'sm' | 'md' | 'lg' | 'compact' | 'small' = 'md';
  @Input() height: number | string = '';
  @Input() fullWidth: boolean = true;
  @Input() clearable: boolean = false;

  @Input() minDate: Date | string | null = null;
  @Input() maxDate: Date | string | null = null;
  @Input() disableFuture: boolean = false;
  @Input() disablePast: boolean = false;

  @Input() customClass: string = '';
  @Input() containerClass: string = '';
  @Input() dateFormat: string = 'MMM dd, yyyy';

  @Input()
  get value(): Date | null {
    return this._value;
  }
  set value(val: Date | string | null) {
    this._value = this.parseDate(val);
    this.updateDisplayValue();
  }

  @Output() dateChange = new EventEmitter<Date | null>();
  @Output() valueChange = new EventEmitter<Date | null>();
  @Output() blur = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();

  private _value: Date | null = null;
  displayValue: string = '';
  isFocused: boolean = false;
  isPickerOpen: boolean = false;

  private onChange: (val: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  get resolvedLabel(): string {
    return this.labelText || this.label;
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  get hasError(): boolean {
    return !!(this.errorMsg || this.errorText);
  }

  get activeError(): string | null {
    return this.errorMsg || this.errorText || null;
  }

  get resolvedHeight(): string | null {
    if (this.height) {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }
    if (this.size === 'compact') return '35px';
    if (this.size === 'small' || this.size === 'sm') return '38px';
    if (this.size === 'lg') return '42px';
    return '38px';
  }

  get minDateParsed(): Date | null {
    if (this.disablePast) return new Date();
    return this.parseDate(this.minDate);
  }

  get maxDateParsed(): Date | null {
    if (this.disableFuture) return new Date();
    return this.parseDate(this.maxDate);
  }

  writeValue(val: any): void {
    this._value = this.parseDate(val);
    this.updateDisplayValue();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  openPicker(event?: Event): void {
    if (this.disabled || this.readonly) return;
    if (event) {
      event.stopPropagation();
    }
    this.picker.open();
  }

  onPickerOpened(): void {
    this.isPickerOpen = true;
    this.isFocused = true;
  }

  onPickerClosed(): void {
    this.isPickerOpen = false;
    this.isFocused = false;
    this.onTouched();
    this.blur.emit();
  }

  onDateSelect(event: MatDatepickerInputEvent<Date>): void {
    const newDate = event.value ? new Date(event.value) : null;
    this._value = newDate;
    this.updateDisplayValue();
    this.onChange(this._value);
    this.dateChange.emit(this._value);
    this.valueChange.emit(this._value);
  }

  handleClear(event: MouseEvent): void {
    event.stopPropagation();
    this._value = null;
    this.displayValue = '';
    this.onChange(null);
    this.dateChange.emit(null);
    this.valueChange.emit(null);
  }

  private parseDate(val: any): Date | null {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val.getTime())) return val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  private updateDisplayValue(): void {
    if (!this._value) {
      this.displayValue = '';
      return;
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const d = this._value.getDate().toString().padStart(2, '0');
    const day = this._value.getDate().toString();
    const m = months[this._value.getMonth()];
    const y = this._value.getFullYear();
    if (this.dateFormat === 'MMM d, yyyy') {
      this.displayValue = `${m} ${day}, ${y}`;
    } else {
      this.displayValue = `${m} ${d}, ${y}`;
    }
  }
}
